import React, { useState } from "react";
import { FaEye, FaEyeSlash, FaLock } from "react-icons/fa";
import CustomDropdown from "../../../atoms/CustomDropdown";
import NumberInput from "../../../atoms/NumberInput";
import TextInput from "../../../atoms/TextInput";
import Checkbox from "../../../atoms/Checkbox";

// Cihaz Checkbox degerini boolean'a normalize eder — read payload bazen
// string ("0"/"false") gonderiyor; ham "!!v" ile bunlar daima acik gorunurdu.
const toBool = (v) => {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    return s === "1" || s === "true" || s === "on" || s === "acik" || s === "açık";
  }
  return !!v;
};

const SettingInput = ({
  setting,
  onChange,
  isChanged = false,
  originalValue = null,
  disabled: readOnly = false,
}) => {
  const [isInvalid, setIsInvalid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const maxValue = setting.upperBound || setting.upperBounde;
  const isDisabled = setting.disabled || readOnly;

  const baseCardClasses = `relative rounded-xl border bg-white transition-all duration-200 px-3 py-2.5 min-w-0 w-full
    ${isChanged ? "border-indigo-300 bg-indigo-50/30 ring-1 ring-indigo-100" : "border-gray-200"}
    hover:border-gray-300`;
  const disabledCardClasses = `relative rounded-xl border border-gray-100 bg-gray-50/80 px-3 py-2.5 opacity-70 min-w-0 w-full`;

  const Label = ({ children }) => (
    <label className="text-xs font-medium text-gray-500 leading-tight" title={setting.label}>
      {children}
    </label>
  );

  const ChangedDot = () =>
    isChanged ? (
      <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-indigo-500" title="Değiştirildi" />
    ) : null;

  const getRegexDescription = (regex) => {
    if (!regex) return "Geçerli bir değer giriniz";
    switch (regex) {
      case "^[0-9]{6}$":
        return "6 haneli sayısal şifre olmalı";
      case "^[0-9]{6,10}$":
        return "6 ile 10 haneli arasında sayısal şifre olmalı";
      case "^(404|000)$":
        return "Sadece 404 veya 000 girebilirsiniz";
      case "^.{1,30}$":
        return "1-30 karakter arasında olmalı";
      case "^.{1,50}$":
        return "1-50 karakter arasında olmalı";
      case "^.+$":
        return "Boş bırakılamaz";
      default: {
        // Genel kalıp tanıma
        let m;
        if ((m = regex.match(/^\^?\[0-9\]\{(\d+),(\d+)\}\$?$/))) {
          return `${m[1]} ile ${m[2]} hane arasında sayı girilmeli`;
        }
        if ((m = regex.match(/^\^?\[0-9\]\{(\d+)\}\$?$/))) {
          return `${m[1]} haneli sayı girilmeli`;
        }
        if ((m = regex.match(/^\^?\.\{(\d+),(\d+)\}\$?$/))) {
          return `${m[1]}-${m[2]} karakter arasında olmalı`;
        }
        if ((m = regex.match(/^\^?\.\{(\d+)\}\$?$/))) {
          return `Tam ${m[1]} karakter olmalı`;
        }
        if ((m = regex.match(/^\^?\(([^)]+)\)\$?$/))) {
          const opts = m[1].split("|").map((s) => `"${s}"`).join(", ");
          return `Sadece şu değerlerden biri olabilir: ${opts}`;
        }
        return "Geçersiz format — lütfen kontrol edin";
      }
    }
  };

  if (isDisabled) {
    return (
      <div className={disabledCardClasses}>
        <div className="flex items-center justify-between mb-1.5">
          <Label>{setting.label}</Label>
          <FaLock className="w-2.5 h-2.5 text-gray-300" />
        </div>
        <div className="text-sm font-semibold text-gray-500">{setting.value}</div>
      </div>
    );
  }

  if (setting.type === "Number") {
    const handleNumberChange = (newValue) => {
      if (newValue === "" || newValue === null || newValue === undefined) {
        setIsInvalid(true);
        onChange(newValue);
        return;
      }
      const numVal = Number(newValue);
      setIsInvalid(numVal < setting.lowerBound || numVal > maxValue);
      onChange(numVal);
    };

    const isEmpty =
      setting.value === "" || setting.value === null || setting.value === undefined;

    const currentIsOutOfBounds = isEmpty
      ? true
      : Number(setting.value) < setting.lowerBound || Number(setting.value) > maxValue;

    return (
      <div className={baseCardClasses}>
        <ChangedDot />
        <Label>{setting.label}</Label>
        <NumberInput
          value={setting.value}
          onChange={handleNumberChange}
          placeholder="—"
          error={isInvalid || currentIsOutOfBounds}
          errorMessage={
            isEmpty
              ? "Boş bırakılamaz"
              : currentIsOutOfBounds
                ? `${setting.lowerBound} – ${maxValue} arası`
                : isInvalid
                  ? `${setting.lowerBound} – ${maxValue} arası`
                  : ""
          }
          wrapperClassName="mt-1"
          className={`!py-1.5 !text-sm !font-semibold ${currentIsOutOfBounds || isInvalid
              ? "!border-amber-400 !bg-amber-50/50"
              : ""
            }`}
        />
        <div className="flex items-center justify-between mt-1.5 text-[11px] text-gray-400">
          <span>{setting.lowerBound}</span>
          <div className="flex-1 mx-2 h-px bg-gray-200" />
          <span>{maxValue}</span>
        </div>
        {isChanged && originalValue !== null && (
          <div className="text-[11px] text-indigo-500 mt-1">
            Önceki: <span className="font-semibold">{originalValue}</span>
          </div>
        )}
      </div>
    );
  }

  if (setting.type === "Checkbox") {
    return (
      <div className={baseCardClasses}>
        <ChangedDot />
        <div className="flex items-center justify-between">
          <Label>{setting.label}</Label>
          <Checkbox
            label={toBool(setting.value) ? "Aktif" : "Pasif"}
            checked={toBool(setting.value)}
            onChange={(e) => onChange(e.target.checked)}
          />
        </div>
        {isChanged && originalValue !== null && (
          <div className="text-[11px] text-indigo-500 mt-1">
            Önceki: <span className="font-semibold">{originalValue ? "Aktif" : "Pasif"}</span>
          </div>
        )}
      </div>
    );
  }

  if (setting.type === "SelectOptions") {
    const dropdownOptions = (setting.options || []).map((option) => {
      if (typeof option === "object") {
        const id =
          option.id ?? option.value ?? option.key ?? option.label ?? JSON.stringify(option);
        return {
          id,
          label: option.label ?? option.name ?? option.value ?? option.id ?? String(id),
          description: option.description,
        };
      }
      return { id: option, label: option };
    });

    const selectedOption =
      dropdownOptions.find(
        (option) =>
          option.id === setting.value ||
          option.label === setting.value ||
          option.id === setting.value?.id
      ) || (setting.value ? { id: setting.value, label: setting.value } : null);

    return (
      <div className={baseCardClasses}>
        <ChangedDot />
        <Label>{setting.label}</Label>
        <CustomDropdown
          value={selectedOption}
          onChange={(option) => onChange(option.id)}
          options={dropdownOptions}
          placeholder="Seçim yapın"
          showSearch={dropdownOptions.length > 6}
          compact={true}
          className="w-full mt-1"
        />
        {isChanged && originalValue !== null && (
          <div className="text-[11px] text-indigo-500 mt-1">
            Önceki: <span className="font-semibold">{originalValue}</span>
          </div>
        )}
      </div>
    );
  }

  if (setting.type === "Input") {
    const handleInputChange = (e) => {
      const value = e?.target?.value ?? "";
      const regex = new RegExp(setting.regex);
      const isValid = regex.test(value);
      setIsInvalid(!isValid);
      onChange(value);
    };

    const isPasswordField =
      setting.regex === "^[0-9]{6}$" || setting.regex === "^[0-9]{6,10}$";

    return (
      <div className={baseCardClasses}>
        <ChangedDot />
        <Label>{setting.label}</Label>
        <TextInput
          value={setting.value || ""}
          onChange={handleInputChange}
          placeholder="—"
          type={isPasswordField && !showPassword ? "password" : "text"}
          error={isInvalid}
          errorMessage={
            isInvalid
              ? getRegexDescription(setting.regex)
              : ""
          }
          className={`!py-1.5 !text-sm ${isInvalid
            ? "!border-red-400 !bg-red-50 focus:!ring-red-100"
            : "!border-gray-200"
            }`}
          wrapperClassName="mt-1"
          endAdornment={
            isPasswordField && (
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="rounded-md bg-gray-100 p-1.5 text-gray-400 transition hover:bg-gray-200 hover:text-gray-600"
                title={showPassword ? "Gizle" : "Göster"}
              >
                {showPassword ? <FaEyeSlash className="h-3 w-3" /> : <FaEye className="h-3 w-3" />}
              </button>
            )
          }
        />
        {isChanged && originalValue !== null && (
          <div className="text-[11px] text-indigo-500 mt-1">
            Önceki: <span className="font-semibold">{originalValue}</span>
          </div>
        )}
      </div>
    );
  }

  if (setting.type === "MultiSelect") {
    const selected = Array.isArray(setting.value) ? setting.value : [];
    const options = Array.isArray(setting.options) ? setting.options : [];
    const toggle = (opt) => {
      const next = selected.includes(opt)
        ? selected.filter((x) => x !== opt)
        : [...selected, opt];
      onChange(next);
    };
    return (
      <div className={`${baseCardClasses} col-span-2`}>
        <ChangedDot />
        <Label>{setting.label}</Label>
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {options.map((opt) => {
            const isOn = selected.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => toggle(opt)}
                className={`px-2 py-1 rounded-md text-xs font-medium border transition-colors ${
                  isOn
                    ? "bg-main text-white border-main"
                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                }`}
              >
                {opt}
              </button>
            );
          })}
        </div>
        {selected.length === 0 && (
          <div className="text-[11px] text-gray-400 mt-1">Hiçbiri seçili değil</div>
        )}
      </div>
    );
  }

  if (setting.type === "TeaFilterCups") {
    const cups = Array.isArray(setting.value) ? setting.value : [];
    const updateCup = (idx, field, fieldValue) => {
      const next = cups.map((c, i) =>
        i === idx ? { ...c, [field]: fieldValue } : c,
      );
      onChange(next);
    };
    return (
      <div className={`${baseCardClasses} col-span-3`}>
        <ChangedDot />
        <Label>{setting.label}</Label>
        {cups.length === 0 ? (
          <div className="text-xs text-gray-400 mt-2">Kayıtlı bardak yok</div>
        ) : (
          <div className="mt-2 space-y-2">
            {cups.map((cup, idx) => (
              <div
                key={cup.id || idx}
                className="rounded-lg border border-gray-200 bg-gray-50/30 p-2"
              >
                <div className="text-xs font-semibold text-main mb-1.5">
                  {cup.name || cup.id || `Bardak ${idx + 1}`}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <label className="flex flex-col">
                    <span className="text-gray-500">Ad</span>
                    <input
                      type="text"
                      value={cup.name || ""}
                      onChange={(e) => updateCup(idx, "name", e.target.value)}
                      className="rounded border border-gray-200 px-1.5 py-0.5 text-sm"
                    />
                  </label>
                  <label className="flex items-center gap-1 col-span-1">
                    <input
                      type="checkbox"
                      checked={!!cup.referenceTea}
                      onChange={(e) =>
                        updateCup(idx, "referenceTea", e.target.checked)
                      }
                    />
                    <span className="text-gray-600">Çay Ref.</span>
                  </label>
                  <label className="flex items-center gap-1 col-span-1">
                    <input
                      type="checkbox"
                      checked={!!cup.referenceFilterCoffee}
                      onChange={(e) =>
                        updateCup(
                          idx,
                          "referenceFilterCoffee",
                          e.target.checked,
                        )
                      }
                    />
                    <span className="text-gray-600">Filtre Ref.</span>
                  </label>
                  <label className="flex flex-col">
                    <span className="text-gray-500">Dolu Ağr. (gr)</span>
                    <input
                      type="number"
                      value={cup.filledWeight ?? 0}
                      onChange={(e) =>
                        updateCup(idx, "filledWeight", Number(e.target.value))
                      }
                      className="rounded border border-gray-200 px-1.5 py-0.5 text-sm"
                    />
                  </label>
                  <label className="flex flex-col">
                    <span className="text-gray-500">Boş Ağr. (gr)</span>
                    <input
                      type="number"
                      value={cup.emptyWeight ?? 0}
                      onChange={(e) =>
                        updateCup(idx, "emptyWeight", Number(e.target.value))
                      }
                      className="rounded border border-gray-200 px-1.5 py-0.5 text-sm"
                    />
                  </label>
                  <label className="flex flex-col">
                    <span className="text-gray-500">Alt Tol. (gr)</span>
                    <input
                      type="number"
                      value={cup.lowerTolerance ?? 0}
                      onChange={(e) =>
                        updateCup(
                          idx,
                          "lowerTolerance",
                          Number(e.target.value),
                        )
                      }
                      className="rounded border border-gray-200 px-1.5 py-0.5 text-sm"
                    />
                  </label>
                  <label className="flex flex-col">
                    <span className="text-gray-500">Üst Tol. (gr)</span>
                    <input
                      type="number"
                      value={cup.upperTolerance ?? 0}
                      onChange={(e) =>
                        updateCup(
                          idx,
                          "upperTolerance",
                          Number(e.target.value),
                        )
                      }
                      className="rounded border border-gray-200 px-1.5 py-0.5 text-sm"
                    />
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
};

export default SettingInput;
